import { createFileRoute } from "@tanstack/react-router";
import { US_STATES } from "@alm/domain";
import { marketingHead } from "@/lib/marketing/seo";
import { breadcrumbs, webPageOf } from "@/lib/marketing/ppl-schema";
import { MLink } from "@/lib/marketing/m-link";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/marketing/reveal";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { SectionHeading } from "@/components/marketing/site/section-heading";
import { MediaFrame } from "@/components/marketing/site/media-frame";
import { MarketingCta } from "@/components/marketing/site/marketing-cta";
import { ProofRequest } from "@/components/marketing/proof/proof-request";
import { SpotlightCopy } from "@/components/marketing/site/spotlight-copy";
import { Home, Brain, Network, ArrowRight, HeartPulse, ClipboardList } from "@/lib/public-icons";
const loungeImg = "/marketing/community_lounge.jpg";
const compassionImg = "/marketing/compassion_hands.jpg";
/* Photo: Far Chinberdiev on Unsplash (unsplash.com/photos/hKM8DiZG9qs), cropped. */
const buildingImg = "/marketing/senior_living_community.jpg";

const solutions = [
  {
    icon: Home,
    eyebrow: "Assisted living",
    title: "Run daily care without the busywork",
    description:
      "Give caregivers a single, secure place for resident records, medications, activities and incidents, so more of the day goes to residents, not paperwork.",
    bullets: [
      "Complete resident and care records",
      "Medication and activity tracking",
      "Daily Roll attendance",
      "Family communication and documents",
    ],
    image: loungeImg,
  },
  {
    icon: Brain,
    eyebrow: "Memory care",
    title: "Structure and safety for higher-acuity care",
    description:
      "Capture incidents, behaviors and care-plan changes with precision, keep authorizations current, and maintain the documentation higher-acuity communities depend on.",
    bullets: [
      "Detailed incident and behavior notes",
      "Care plan and checklist tracking",
      "Medicaid / MCO authorization tracking",
      "Audit-ready, role-based records",
    ],
    image: compassionImg,
  },
  {
    icon: Network,
    eyebrow: "Multi-facility operators",
    title: "One platform across every community",
    description:
      "Standardize operations across locations with per-tenant data isolation, an All Facilities view for leadership and consolidated financial and census reporting.",
    bullets: [
      "All Facilities leadership view",
      "Per-facility data isolation",
      "Consolidated financial reporting",
      "Cross-facility tasks and CRM",
    ],
    image: buildingImg,
  },
];

/* What arrives for the reader's state, named before they type: Medicaid enrollment, authorizations,
 * renewals and state forms live on the record on the state's timeline (as /operating-system says).
 * Operators would not give a number without knowing what came back. */
const stateReceipts = [
  {
    title: "Your state's Medicaid workflow, in writing",
    description:
      "How enrollment, managed-care authorizations and renewals run on the record for your state, the forms it asks for and the deadlines the move-in checklist carries.",
  },
  {
    title: "What that looks like at move-in",
    description:
      "Which authorizations gate the move-in, when each is due on your state's timeline, and where the paperwork behind it is filed on the record.",
  },
  {
    title: "No call attached",
    description:
      "This is a written answer about your state. Book a demo when you want one, not to get this.",
  },
];

const outcomes = [
  {
    icon: HeartPulse,
    title: "More time at the bedside",
    description:
      "Less double-entry and chasing paperwork means caregivers spend their time on residents.",
  },
  {
    icon: ClipboardList,
    title: "Always inspection-ready",
    description:
      "Structured records, checklists and audit trails keep documentation complete and current.",
  },
  {
    icon: Network,
    title: "Scales with your portfolio",
    description: "Add communities, staff and modules without re-platforming or starting over.",
  },
];

function MarketingSolutions() {
  return (
    <MarketingShell>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative mx-auto max-w-6xl px-4 pt-20 pb-14 text-center md:pt-28">
          <Reveal>
            <p
              className="mb-5 text-sm font-medium text-muted-foreground"
              data-testid="badge-solutions"
            >
              Built around how care really works
            </p>
          </Reveal>
          <Reveal delay={60}>
            <h1
              className="font-display mx-auto max-w-4xl text-balance text-4xl font-medium leading-[1.02] tracking-[-0.035em] md:text-6xl"
              data-testid="text-solutions-title"
            >
              Solutions for every kind of <span className="text-gradient-warm">community</span>
            </h1>
          </Reveal>
          <Reveal delay={120}>
            <p className="mx-auto mt-6 max-w-2xl text-pretty text-lg text-muted-foreground">
              Whether you run a single assisted living home or a portfolio of communities, PEOPLE AI
              Workforce adapts to your model, not the other way around.
            </p>
          </Reveal>
          {/* The question this page is actually asked. A reader deciding whether a platform is "for
              someone like me" is usually deciding on their own state's rules, and that answer was
              five sections down with nothing to click. */}
          <Reveal delay={180}>
            <p className="mt-6 text-sm text-muted-foreground">
              Wondering whether it fits your state's rules?{" "}
              <a
                href="#state-check"
                className="font-medium text-primary underline-offset-4 hover:underline"
                data-testid="link-solutions-state-jump"
              >
                Ask where your state stands
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* Solution spotlights */}
      <section>
        <div className="mx-auto max-w-6xl space-y-24 px-4 py-20 md:py-28">
          {solutions.map((s, i) => {
            const reversed = i % 2 === 1;
            return (
              <Reveal key={s.title}>
                <div className="grid items-center gap-10 md:grid-cols-2">
                  <MediaFrame
                    src={s.image}
                    alt={s.title}
                    className={reversed ? "md:order-2" : ""}
                    testId={`img-solution-${i}`}
                  />
                  <div className={reversed ? "md:order-1" : ""}>
                    <SpotlightCopy
                      icon={s.icon}
                      eyebrow={s.eyebrow}
                      title={s.title}
                      description={s.description}
                      bullets={s.bullets}
                    />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* The ask sits where the question forms: whether this fits a community under the reader's
          own state rules, raised by "Medicaid / MCO authorization tracking" in the spotlight
          above. */}
      <ProofRequest
        formTag="state-check"
        testId="solutions-state"
        id="state-check"
        /* The outcomes band below is already bg-card/40; two of them stacked
           read as one long section and the ask loses its edges. */
        className="bg-background"
        eyebrow="Medicaid in your state"
        title="Built for Medicaid. Tell us your state."
        subtitle="Medicaid enrollment, managed-care authorizations, renewals and your state's forms live on the record, on your state's timeline. Tell us which state you operate in and we write back with how your Medicaid workflow runs in PEOPLE AI Workforce, the forms, the deadlines and what the move-in checklist carries."
        select={{
          name: "state",
          label: "Which state do you operate in?",
          placeholder: "Select your state",
          options: US_STATES,
          summaryLabel: "Asking about Medicaid in:",
          hint: "Pick the state whose survey you answer to. Running in more than one? Pick the one that decides this, and name the rest below.",
          unpickedHint: "Pick a state, so the answer is about your Medicaid program.",
        }}
        receipts={stateReceipts}
        noteLabel="Anything specific about your state's rules? (optional)"
        notePlaceholder="e.g. the assessment form our surveyor asks for, or the waiver we bill…"
        submitLabel="Ask about my state"
        successTitle="We have your state"
        successBody="Your question carries the state you chose and the community you typed. One of us will write back to the address you gave with how your state's Medicaid workflow runs on the record."
        footnote="A written answer about your state. Not a sales call."
      />

      {/* Outcomes */}
      <section className="border-t border-border/60 bg-card/40">
        <div className="mx-auto max-w-6xl px-4 py-20 md:py-28">
          <Reveal>
            <SectionHeading eyebrow="The outcome" title="What teams gain on day one" />
          </Reveal>
          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {outcomes.map((o, i) => (
              <Reveal key={o.title} delay={i * 80}>
                <div className="flex h-full flex-col rounded-xl border border-border/60 bg-background p-6 hover-elevate">
                  <span className="inline-flex size-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <o.icon className="size-5" />
                  </span>
                  <h3 className="mt-4 text-base font-semibold tracking-tight">{o.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{o.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <div className="mt-10 flex justify-center">
              <Button
                variant="outline"
                className="font-ui"
                data-testid="button-solutions-features"
                asChild
              >
                <MLink href="/operating-system">
                  Explore all features
                  <ArrowRight className="ml-2 size-4" />
                </MLink>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <MarketingCta
        title="Find the fit for your community"
        subtitle="Book a demo and see how PEOPLE AI Workforce shapes around the way your team already works."
        secondaryLabel="Talk to us"
        secondaryHref="/contact"
      />
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/solutions")({
  component: MarketingSolutions,
  head: () => {
    const seo = {
      title: "Solutions: assisted living, memory care and multi-facility | PEOPLE AI Workforce",
      description:
        "Built around how care really works: daily care records and medication tracking for assisted living, incident and authorization documentation for memory care, and per-facility isolation with an All Facilities view and consolidated reporting for operators running more than one community.",
      path: "/v2/solutions",
      imageAlt: "PEOPLE AI Workforce. The operating system for assisted living communities",
    };
    return marketingHead({
      ...seo,
      schema: [webPageOf(seo), breadcrumbs({ name: "Solutions", path: "/solutions" })],
    });
  },
});
